import { useTranslation } from 'react-i18next'
import {
  batteryOemBrand,
  estimateNormalUseEnduranceHours,
  formatBatteryTestedDate,
  hasBatteryTransparency,
  type BatteryTransparencyInput,
} from '@redevicer/shared'

type Props = {
  listing: BatteryTransparencyInput
  showKicker?: boolean
}

type Fact = { key: string; icon: string; text: string }

export default function BatteryTransparencyPanel({ listing, showKicker = true }: Props) {
  const { t, i18n } = useTranslation()
  if (!hasBatteryTransparency(listing)) return null

  const health = listing.battery_health as number
  const hours = estimateNormalUseEnduranceHours(health, listing.manufacturer)
  const tested = formatBatteryTestedDate(listing.battery_tested_at, i18n.language)
  const brand = batteryOemBrand(listing.manufacturer)
  const hoursLabel =
    hours != null ? (Number.isInteger(hours) ? String(hours) : hours.toFixed(1)) : null
  const fill = Math.min(100, Math.max(4, health))

  const facts: Fact[] = []
  if (listing.battery_cycles != null && listing.battery_cycles >= 0) {
    facts.push({
      key: 'cycles',
      icon: '↻',
      text: t('batteryTransparency.cycles', { count: listing.battery_cycles }),
    })
  }
  if (listing.battery_original === true) {
    facts.push({
      key: 'original',
      icon: '✓',
      text: t('batteryTransparency.original', { brand }),
    })
  } else if (listing.battery_original === false) {
    facts.push({
      key: 'replaced',
      icon: '↺',
      text: t('batteryTransparency.replaced'),
    })
  }
  if (hoursLabel) {
    facts.push({
      key: 'endurance',
      icon: '⏱',
      text: t('batteryTransparency.endurance', { hours: hoursLabel }),
    })
  }
  if (tested) {
    facts.push({
      key: 'tested',
      icon: '◷',
      text: t('batteryTransparency.tested', { date: tested }),
    })
  }

  return (
    <section className="battery-transparency" aria-label={t('batteryTransparency.title')}>
      <div className="battery-transparency__glow" aria-hidden="true" />
      <header className="battery-transparency__head">
        {showKicker ? (
          <span className="battery-transparency__kicker seller-declared-kicker">
            {t('batteryTransparency.kicker')}
          </span>
        ) : null}
        <h3 className="battery-transparency__title">
          Battery Transparency
          <sup className="battery-transparency__tm">™</sup>
        </h3>
      </header>

      <div className="battery-transparency__hero">
        <div className="battery-transparency__cell" aria-hidden="true">
          <svg className="battery-transparency__icon" viewBox="0 0 52 88" role="img">
            <rect x="16" y="2" width="20" height="8" rx="2.5" fill="currentColor" opacity="0.45" />
            <rect x="4" y="10" width="44" height="74" rx="10" fill="none" stroke="currentColor" strokeWidth="3" />
            <rect
              className="battery-transparency__fill"
              x="9"
              y={16 + ((100 - fill) / 100) * 62}
              width="34"
              height={(fill / 100) * 62}
              rx="6"
            />
          </svg>
        </div>
        <div className="battery-transparency__hero-copy">
          <p className="battery-transparency__pct">
            {health}
            <span>%</span>
          </p>
          <p className="battery-transparency__pct-label">{t('batteryTransparency.healthLabel')}</p>
          <div className="battery-transparency__meter" aria-hidden="true">
            <span className="battery-transparency__meter-fill" style={{ width: `${fill}%` }} />
          </div>
        </div>
      </div>

      {facts.length > 0 ? (
        <ul className="battery-transparency__facts">
          {facts.map((fact, i) => (
            <li key={fact.key} style={{ animationDelay: `${0.28 + i * 0.08}s` }}>
              <span className="battery-transparency__fact-icon" aria-hidden="true">
                {fact.icon}
              </span>
              <span>{fact.text}</span>
            </li>
          ))}
        </ul>
      ) : null}
    </section>
  )
}
